import React, { useEffect, useState } from 'react'
import { Switch, Route, withRouter } from 'react-router-dom'

import CityPlanView from '../../Containers/Campaigns/CityPlanView'
import RosterView from '../../Containers/Campaigns/RosterView'
import CityPlanEdit from '../../Containers/Campaigns/CityPlanEdit'
import { AdminRoute, PrivateRoute } from '../../Config/Routing/auth'
import { PATHS } from '../../Config/Routing/paths'
import LetterboxMissions from '../../Containers/LetterboxMissions/container/LetterboxMissions'
import { normalizeGraphQLConnection } from '../../shared/graphql/utils'
import { mockRosters } from '../../Utils/Mission'
import {
  callAuthorizedGraphQLEndpoint,
  fetchCityPlanMissionFormChunks,
} from '../../Module/graphQL'
import LoadingIndicator from '../../Components/LoadingIndicator/LoadingIndicator'

const CityPlanRouter = props => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [campaign, setCampaign] = useState({})

  const { campaignId, cityPlanId, rosterId } = props.match.params

  useEffect(() => {
    fetchData({ campaignId, cityPlanId })
  }, [])

  const fetchData = async ({ campaignId, cityPlanId }) => {
    setIsLoaded(false)
    try {
      const result = await getCityPlanData({
        campaignId,
        cityPlanId,
        missionChunk: 200,
      })

      const formattedData = formatData(result)
      setCampaign(formattedData)
    } catch (e) {
      setCampaign(null)
      throw e
    } finally {
      setIsLoaded(true)
    }
  }

  if (!isLoaded) return <LoadingIndicator />

  return (
    <Switch>
      <Route exact path={props.match.path}>
        <PrivateRoute>
          <CityPlanView {...props} {...campaign} />
        </PrivateRoute>
      </Route>
      <Route
        exact
        path={`${props.match.path}${PATHS.cityPlan.letterboxMissions}`}
      >
        <PrivateRoute>
          <LetterboxMissions
            refetchMissions={(args = {}) =>
              fetchData({ campaignId, cityPlanId, ...args })
            }
            {...props}
            {...campaign}
          />
        </PrivateRoute>
      </Route>
      <Route exact path={`${props.match.path}${PATHS.cityPlan.rosterView}`}>
        <PrivateRoute>
          <RosterView
            {...props}
            {...campaign}
            missions={
              rosterId && getRostersMissions(rosterId, campaign.rosters)
            }
            refetchMissions={(args = {}) =>
              fetchData({ campaignId, cityPlanId, ...args })
            }
          />
        </PrivateRoute>
      </Route>
      <Route exact path={`${props.match.path}${PATHS.cityPlan.cityPlanEdit}`}>
        <AdminRoute>
          <CityPlanEdit {...props} {...campaign} />
        </AdminRoute>
      </Route>
    </Switch>
  )
}

const getCityPlanData = async ({ campaignId, cityPlanId, missionChunk }) => {
  const gqlCampaign = `query 
    (
    $campaignId:ID!
    ) {
      campaign(id:$campaignId){
        id
        name
        type
        status
        metrics {
          nodes {
            id
            name
            targetConversionRate
            targetCostPerMetric
            isPrimary 
          }
        }
      }
    }`

  const gqlCityPlan = `
  query ($cityPlanId:ID!) {
    cityPlans(cityPlanId: $cityPlanId) {
      edges {
          node {
            id
            teamLeaderPayAdjustment
            hourlyPayout
            clientMarginPercent
            distributionCost
            payout
            city {
              id
              name
              latitude
              longitude
              lunchCheckInTime
              afternoonCheckInTime
              teamLeaderEnabled
              locations{
                edges {
                  node {
                    id
                    name
                    longitude
                    latitude
                  }
                }
              }
              country {
                code
                currencySign 
              }
            }
          }
      }
    }
  }
`
  const gqlMissions = `
   query ($cityPlanId:ID!, $first: Int, $skip: Int) {
    cityPlans(cityPlanId: $cityPlanId) {
      edges {
        node {
           id
            missions(first: $first, skip: $skip) {
              pageInfo {
                hasNextPage
              }
              edges {
                node {
                  id
                  flyerCode
                  status
                  totalFlyersDistributed
                  dateTime
                  distributionCost
                  draftPayAdjustment
                  printingCostPerFlyer
                  bonusPerConversion
                  bonusRedemptionPeriod
                  cancelled
                  automatic
                  type
                  boundaryColour
                  label
                  packSize
                  boundary {
                    latitude
                    longitude
                  }
                  user {
                    id
                    firstName
                    lastName
                  }
                  location {
                    id
                    name
                    detailedRank
                  }
                  costPerFlyer
                  amountScans
                  canBeSpied
                  spyInformation{
                    spy{
                      id
                      firstName
                      lastName
                    }
                    spyDetails{
                      checkInTime
                      checkoutTime
                      reportReason
                      }
                    shift{
                      id
                    }
                  }
                }
              }
            }
        }
      }
    }
  }
  `

  const campaignResponse = await callAuthorizedGraphQLEndpoint(gqlCampaign, {
    campaignId,
  })
  const campaign = await campaignResponse.json()
  const campaignResult = campaign.data.campaign

  const getCityPlan = async () => {
    const cityPlanResponse = await callAuthorizedGraphQLEndpoint(gqlCityPlan, {
      cityPlanId,
    })
    const resCityPlanResponse = await cityPlanResponse.json()
    const cityPlan = resCityPlanResponse.data.cityPlans.edges[0].node

    const { missions } = await fetchCityPlanMissionFormChunks({
      cityPlanId,
      chunkSize: missionChunk,
      skip: 0,
      gql: gqlMissions,
    })

    return {
      ...cityPlan,
      missions: {
        edges: missions,
      },
    }
  }

  const cityPlan = await getCityPlan()

  return { ...campaignResult, cityPlans: { nodes: cityPlan } }
}

const formatData = campaignData => {
  if (!campaignData) {
    return {
      campaign: {},
      cityPlan: {},
      rosters: [],
      metrics: [],
    }
  }
  const cityPlan = campaignData.cityPlans.nodes

  const cityPlanMissions = normalizeGraphQLConnection(cityPlan.missions)
  const rosters = mockRosters(cityPlanMissions)

  return {
    campaign: campaignData,
    cityPlan: {
      ...cityPlan,
      missions: cityPlanMissions,
    },
    city: cityPlan.city,
    rosters,
    countryCode: cityPlan.city.country.code,
  }
}

const getRostersMissions = (rosterId, rosters) => {
  const [year, week] = rosterId.split('-')
  const roster = rosters.find(
    roster => roster.weekNum == week && roster.year == year,
  )
  return roster && roster.missions ? roster.missions : []
}

export default withRouter(CityPlanRouter)



// WEBPACK FOOTER //
// ./src/Config/Routing/CityPlanRouter.js